"use client";
import { type ReactNode } from "react";
import { EthereumWalletConnectors } from "@dynamic-labs/ethereum";
import { DynamicContextProvider } from "@dynamic-labs/sdk-react-core";
import { DynamicWagmiConnector } from "@dynamic-labs/wagmi-connector";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { http } from "viem";
import { berachain, mainnet } from "viem/chains";
import { cookieStorage, createConfig, createStorage, WagmiProvider } from "wagmi";
import { env } from "@/lib/env";

const queryClient = new QueryClient();

// Ensure RPC URLs are properly configured
const mainnetRpcUrl = env.NEXT_PUBLIC_MAINNET_RPC_URL;
const berachainRpcUrl = env.NEXT_PUBLIC_BERA_RPC_URL;

// Configure Wagmi with explicit RPC URLs as required by Dynamic Labs docs
const config = createConfig({
  storage: createStorage({
    storage: cookieStorage,
  }),
  ssr: true, // Enable SSR mode for Next.js
  chains: [mainnet, berachain],
  multiInjectedProviderDiscovery: false, // Dynamic handles this
  transports: {
    [mainnet.id]: http(mainnetRpcUrl),
    [berachain.id]: http(berachainRpcUrl),
  },
});

export default function Providers({ children }: { children: ReactNode }) {
  // Dynamic v4 cookie-based auth configuration
  // If NEXT_PUBLIC_DYNAMIC_COOKIE_DOMAIN is set (production), use custom auth domain
  // Otherwise (local dev), use default Dynamic endpoint
  const dynamicApiBaseUrl = env.NEXT_PUBLIC_DYNAMIC_COOKIE_DOMAIN
    ? `https://${env.NEXT_PUBLIC_DYNAMIC_COOKIE_DOMAIN}/api/v0`
    : undefined; // Let Dynamic use default

  // Regular auth flow or authenticated iframe
  return (
    <DynamicContextProvider
      settings={{
        environmentId: env.NEXT_PUBLIC_DYNAMIC_ENVIRONMENT_ID,
        initialAuthenticationMode: "connect-and-sign",
        ...(dynamicApiBaseUrl && { apiBaseUrl: dynamicApiBaseUrl }), // Add apiBaseUrl only if custom domain is set
        social: { strategy: "popup" },
        walletConnectors: [EthereumWalletConnectors],
      }}
    >
      <WagmiProvider config={config}>
        <QueryClientProvider client={queryClient}>
          <DynamicWagmiConnector>{children}</DynamicWagmiConnector>
        </QueryClientProvider>
      </WagmiProvider>
    </DynamicContextProvider>
  );
}
